import React, {useState, useEffect} from "react";
import clinicbookingService from "../../../../../services/clinicbooking.service";
import { Button, Card, Form, Table, Pagination, Badge, Row, Col, Modal} from "react-bootstrap";
import Swal from "sweetalert2";

function CustomHours({doctorid, clinicid, userid}) {
    const [customHours, setCustomHours] = useState([]);
    const [editingRow, setEditingRow] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalElements, setTotalElements] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        workDate: "",
        isOff: false,
        startTime: "10:00:00",
        endTime: "17:00:00",
        lunchFrom: "13:00:00",
        lunchTo: "13:30:00"
    });

    useEffect(() => {
        getHours(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const getHours = (pageNo, size) => {
        clinicbookingService
            .getCustomHours(clinicid, doctorid, pageNo - 1, size)
            .then((res) => {
                setCustomHours(res.data.content);
                setTotalPages(res.data.totalPages);
                setTotalElements(res.data.totalElements);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const handleAddNew = () => {
        setShowModal(true);
        setIsAdding(true);
        setFormData({
            workDate: "",
            isOff: false,
            startTime: "10:00:00",
            endTime: "17:00:00",
            lunchFrom: "13:00:00",
            lunchTo: "13:30:00"
        });
    };

    const handleEdit = (row) => {
        setEditingRow(row.id);
        setFormData({
            workDate: row.workDate,
            isOff: row.isOff,
            startTime: row.startTime,
            endTime: row.endTime,
            lunchFrom: row.lunchFrom,
            lunchTo: row.lunchTo
        });
        setShowModal(true);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-danger btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm me-2'
            },
            buttonsStyling: false
        }).then((result) => {
            if (result.isConfirmed) {
                clinicbookingService
                    .deleteCustomHour(clinicid, doctorid, id)
                    .then((res) => {
                        // Calculate what the new page should be
                        const isLastItemOnCurrentPage = customHours.length === 1;
                        const shouldGoToPreviousPage = isLastItemOnCurrentPage && currentPage > 1;

                        let newPage = currentPage;
                        if (shouldGoToPreviousPage) {
                            newPage = currentPage - 1;
                        }

                        // Update current page first
                        setCurrentPage(newPage);

                        // Then fetch data for the new page
                        getHours(newPage, pageSize);

                        Swal.fire(
                            'Deleted!',
                            'The custom hour has been deleted.',
                            'success'
                        );
                    })
                    .catch((err) => {
                        console.error("Error Deleting Row", err);

                        // Even if delete fails, try to refresh the data
                        getHours(currentPage, pageSize);

                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: 'Failed to delete custom hour'
                        });
                    });
            }
        });
    };

    const handleSave = () => {
        let dataToSave = {...formData};

        // Format date properly
        if (dataToSave.workDate) {
            const dateObject = new Date(dataToSave.workDate);
            const year = dateObject.getFullYear();
            const month = String(dateObject.getMonth() + 1).padStart(2, "0");
            const day = String(dateObject.getDate()).padStart(2, "0");
            dataToSave.workDate = `${year}-${month}-${day}`;
        }

        dataToSave.userId = userid;
        dataToSave.doctorId = doctorid;
        dataToSave.clinicId = clinicid;

        clinicbookingService
            .addCutomHour(clinicid, doctorid, dataToSave)
            .then((res) => {
                setShowModal(false);
                setEditingRow(null);
                setIsAdding(false);

                // Refresh the data from server instead of adding locally
                getHours(currentPage, pageSize);

                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `Custom hour ${isAdding ? 'added' : 'updated'} successfully`
                });
            })
            .catch((err) => {
                console.error("Error saving custom hour", err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: `Failed to ${isAdding ? 'add' : 'update'} custom hour`
                });
            });
    };

    const handleInputChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        const [year, month, day] = dateString.split("-").map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));

        // Format the date as "5 Aug, 2025"
        const dayPart = date.getUTCDate();
        const monthPart = date.toLocaleString('default', { month: 'short' });
        const yearPart = date.getUTCFullYear();

        return `${dayPart} ${monthPart}, ${yearPart}`;
    };

    return (
        <>
            <div className="shadow-none card-border card card-padding">
                <div className="card-title d-flex justify-content-between align-items-center">
                    <div>
                        <i className="bi bi-clock me-2"></i>
                        Custom Hours
                    </div>
                    <button className="btn btn-outline-primary btn-sm" onClick={handleAddNew}>
                        <i className="bi-plus-circle me-1"></i> Add Custom Hour
                    </button>
                </div>
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center small">
                            <span className="me-2">Show</span>
                            <Form.Select
                                size="sm"
                                style={{width: '80px'}}
                                value={pageSize}
                                onChange={handlePageSizeChange}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="15">15</option>
                            </Form.Select>
                            <span className="ms-2">entries</span>
                        </div>
                        <div>
                            <span className="small text-secondary">(*24 Hours clock)</span>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-bordered table-striped table-hover">
                            <thead>
                            <tr>
                                <th>Work Date</th>
                                <th>Status</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Lunch From</th>
                                <th>Lunch To</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody className="small">
                            {customHours.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-4">
                                        No custom hours found. Click "Add Custom Hour" to create one.
                                    </td>
                                </tr>
                            ) : (
                                customHours.map((row) => (
                                    <tr key={row.id}>
                                        <td>{formatDate(row.workDate)}</td>
                                        <td className="small">
                                            {row.isOff ? (
                                                <span className="badge service-status status-incomplete text-danger"><i className="bi bi-x-circle"></i> Off</span>
                                            ) : (
                                                <span className="badge service-status status-completed text-success"><i className="bi bi-check-circle"></i> Working</span>
                                            )}
                                        </td>
                                        <td>{row.startTime}</td>
                                        <td>{row.endTime}</td>
                                        <td>{row.lunchFrom}</td>
                                        <td>{row.lunchTo}</td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handleEdit(row)}
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDelete(row.id)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                        <div className="small text-secondary">
                            Showing {customHours.length} of {totalElements} entries
                        </div>
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />

                            {[...Array(totalPages)].map((_, index) => {
                                const page = index + 1;
                                // Show limited pagination items with ellipsis
                                if (
                                    page === 1 ||
                                    page === totalPages ||
                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                ) {
                                    return (
                                        <Pagination.Item
                                            key={page}
                                            active={page === currentPage}
                                            onClick={() => handlePageChange(page)}
                                        >
                                            {page}
                                        </Pagination.Item>
                                    );
                                } else if (page === currentPage - 2 || page === currentPage + 2) {
                                    return <Pagination.Ellipsis key={page}/>;
                                }
                                return null;
                            })}

                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isAdding ? 'Add Custom Hour' : 'Edit Custom Hour'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Work Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="workDate"
                                        value={formData.workDate}
                                        onChange={handleInputChange}
                                        required={true}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Check
                                        type="checkbox"
                                        name="isOff"
                                        label="Day Off"
                                        checked={formData.isOff}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {!formData.isOff && (
                            <>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Start Time</Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="startTime"
                                                value={formData.startTime}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>End Time</Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="endTime"
                                                value={formData.endTime}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Lunch From</Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="lunchFrom"
                                                value={formData.lunchFrom}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Lunch To</Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="lunchTo"
                                                value={formData.lunchTo}
                                                onChange={handleInputChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-danger btn-sm" onClick={() => setShowModal(false)} >
                        <i className="bi bi-x"></i> Cancel
                    </button>
                    <button className="btn btn-success btn-sm" onClick={handleSave} >
                        <i className="bi bi-floppy"></i> Save
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default CustomHours;